import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CallRecordsService } from '../../../core/services/call-records/call-records.service';
import { CallStats } from '../../../core/interfaces/communication.interface';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

@Component({
  selector: 'app-voip-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule],
  templateUrl: './voip-dashboard.component.html',
  styleUrl: './voip-dashboard.component.css',
})
export class VoipDashboardComponent implements OnInit, AfterViewInit {
  stats: CallStats | null = null;
  isLoading = true;

  @ViewChild('hourlyChart') hourlyChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('dispositionChart') dispositionChartRef!: ElementRef<HTMLCanvasElement>;

  private hourlyChart: Chart | null = null;
  private dispositionChart: Chart | null = null;

  constructor(
    private callRecordsService: CallRecordsService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadStats();
  }

  ngAfterViewInit(): void {}

  loadStats(): void {
    this.isLoading = true;
    this.callRecordsService.getStats().subscribe({
      next: (res) => {
        this.stats = res.data;
        this.isLoading = false;
        setTimeout(() => this.renderCharts(), 100);
      },
      error: () => this.isLoading = false,
    });
  }

  formatDuration(seconds: number | null): string {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  formatTotalTime(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }

  private renderCharts(): void {
    if (!this.stats) return;
    this.renderHourlyChart();
    this.renderDispositionChart();
  }

  private renderHourlyChart(): void {
    if (!this.hourlyChartRef?.nativeElement || !this.stats) return;
    if (this.hourlyChart) this.hourlyChart.destroy();

    const ctx = this.hourlyChartRef.nativeElement.getContext('2d');
    if (!ctx) return;

    this.hourlyChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.stats.by_hour.map(h => `${h.hour}:00`),
        datasets: [{
          label: this.translate.instant('communication.voip.calls'),
          data: this.stats.by_hour.map(h => h.count),
          backgroundColor: 'rgba(85, 110, 230, 0.7)',
          borderRadius: 4,
          barThickness: 24,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 5 } },
          x: { grid: { display: false } },
        },
      },
    });
  }

  private renderDispositionChart(): void {
    if (!this.dispositionChartRef?.nativeElement || !this.stats) return;
    if (this.dispositionChart) this.dispositionChart.destroy();

    const ctx = this.dispositionChartRef.nativeElement.getContext('2d');
    if (!ctx) return;

    const dispositions = this.stats.by_disposition;
    const labels = Object.keys(dispositions).map(k =>
      this.translate.instant('communication.voip.disposition_' + k)
    );
    const data = Object.values(dispositions);
    const colors = [
      '#34c38f', '#50a5f1', '#f1b44c', '#f46a6a', '#556ee6',
      '#74788d', '#33b5e5', '#e83e8c', '#343a40', '#6f42c1', '#20c997',
    ];

    this.dispositionChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: colors.slice(0, data.length),
          borderWidth: 2,
          borderColor: '#fff',
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: { font: { size: 11 }, padding: 12 },
          },
        },
      },
    });
  }
}
